import React from 'react';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IInvoiceLineSelectionDialogProperties } from './InvoiceLineSelectionDialog.properties';
import { InvoiceLineSelectionDialogStyles } from './InvoiceLineSelectionDialog.styles';
import { withStyles } from '@material-ui/core/styles';
import DataGrid from 'devextreme-react/data-grid';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import { columns } from './InvoiceLineSelectionDialog.constants';
import { IFormViewModelHandle } from 'components/common/Modals/FormViewModal.properties';
import { HotKeys } from 'react-hotkeys';
import { useMemoizedKeyHandlers } from 'utils/hooks';
import { isNull } from 'utils/utils';
import { ENTER_KEY, ESCAPE, TAB } from 'utils/constants';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';

const InvoiceLineSelectionDialog = (props: IInvoiceLineSelectionDialogProperties) => {
    const {
        classes, open, loading, onClose, onOk, dialogStyles, title, subTitle, invoiceLines
    } = props;

    const gridRef = React.useRef<DataGrid>();
    const formViewModalRef = React.useRef<IFormViewModelHandle>();
    const [selectedRow, setSelectedRow] = React.useState<any>(null);
    const [gridLength, setGridLength] = React.useState(0);
    const okButtonRef = React.useRef<HTMLButtonElement & { isSubmitting?: boolean }>();
    const cancelButtonRef = React.useRef<HTMLButtonElement>();
    const lastToggleButtonRef = React.useRef<HTMLInputElement>();

    const getActions = React.useCallback(
        (_onClose, _onOk, _gridLength: number, isRowSelected: boolean, _okButtonRef, _cancelButtonRef) => {

            return [
                {
                    title: 'Ok',
                    iconName: 'CheckCircle',
                    isDefault: (_gridLength > 0),
                    disabled: (_gridLength === 0 || !isRowSelected),
                    ref: _okButtonRef,
                    listener: () => {
                        if (_onOk) {
                            _onOk(selectedRow);
                        }
                    }
                },
                {
                    title: 'Cancel',
                    iconName: 'Cancel',
                    ref: _cancelButtonRef,
                    listener: () => {
                        if (_onClose) {
                            _onClose();
                        }
                    },
                },
            ];
        },
        [selectedRow]
    );

    React.useEffect(
        () => {
            setGridLength(gridRef.current?.instance?.getDataSource()?.items().length);
        },
        [gridRef.current?.instance?.getDataSource()?.items().length]
    );

    const actions = React.useMemo(
        () => getActions(onClose, onOk, gridLength, !isNull(selectedRow), okButtonRef, cancelButtonRef),
        [onOk, onClose, gridLength, selectedRow]
    );

    const handleOnSelectionChanged = React.useCallback(
        (e) => {
            setSelectedRow(e.selectedRowsData || null);
        },
        []
    );

    const onEscape = React.useCallback(
        () => {
            if (onClose) {
                onClose();
            }
        },
        [onClose]
    );

    const onEnter = React.useCallback(
        () => {
            if (okButtonRef.current &&
                gridRef.current.instance?.$element()?.[0]?.contains(document.activeElement)) {
                onOk(selectedRow);
            }
        },
        [okButtonRef.current, selectedRow, onOk]
    );

    const handleGridKeyDown = React.useCallback(
        (e) => {
            const keyboardEvent = e.event as KeyboardEvent;
            const dataGridInstance = gridRef.current?.instance;

            if (!dataGridInstance) return;

            const currentRowIndex = dataGridInstance.option('focusedRowIndex');
            const currentColumnIndex = dataGridInstance.option('focusedColumnIndex');

            if (currentRowIndex === undefined || currentColumnIndex === undefined) return;

            const COLUMNS_COUNT = 10;

            const focusAndScrollToCell = (rowIndex, columnIndex) => {
                const cellElement = dataGridInstance.getCellElement(rowIndex, columnIndex);
                if (cellElement) {
                    dataGridInstance.option('focusedColumnIndex', columnIndex);
                    dataGridInstance.focus(cellElement);
                    dataGridInstance.getScrollable().scrollToElement(cellElement);
                }
            };

            if (keyboardEvent.key === 'ArrowRight') {
                keyboardEvent.preventDefault();
                const nextColumnIndex = currentColumnIndex + 1;
                if (nextColumnIndex < dataGridInstance.columnCount()) {
                    focusAndScrollToCell(currentRowIndex, nextColumnIndex);
                }

                return;
            }

            if (keyboardEvent.key === 'ArrowLeft') {
                keyboardEvent.preventDefault();
                const previousColumnIndex = currentColumnIndex - 1;

                if (previousColumnIndex >= 0) {
                    if (previousColumnIndex < COLUMNS_COUNT) {
                        dataGridInstance.getScrollable().scrollTo({ x: 0 });
                    }
                    focusAndScrollToCell(currentRowIndex, previousColumnIndex);
                }

                return;
            }

            if (keyboardEvent.keyCode === TAB && keyboardEvent.shiftKey) {
                if (currentColumnIndex <= COLUMNS_COUNT) {
                    keyboardEvent.preventDefault();
                    dataGridInstance.getScrollable().scrollTo({ x: 0 });

                    return;
                }

                return;
            }

            if (keyboardEvent.keyCode === TAB && !keyboardEvent.shiftKey) {
                handleFocusToToggle();

                return;
            }

            if (keyboardEvent.keyCode === ESCAPE) {
                onEscape();

                return;
            }

            if (keyboardEvent.keyCode === ENTER_KEY) {
                onEnter();

                return;
            }
        },
        [onEscape, onEnter, formViewModalRef, gridRef]
    );

    const handlers = useMemoizedKeyHandlers({
        Escape: onEscape,
        Enter: onEnter
    });

    const handleFocusToToggle = React.useCallback(
        () => {
            setTimeout(() => lastToggleButtonRef.current?.focus(), 0);
        },
        [lastToggleButtonRef.current]
    );

    return (
        <div>
            <HotKeys handlers={handlers}>
                <FormViewModal
                    open={open}
                    dialogClassName={classes.InvoiceLineSelectionDialogModal}
                    title={title}
                    subTitle={subTitle}
                    loading={loading}
                    innerRef={formViewModalRef}
                    onActionShiftTabOut={handleFocusToToggle}
                    dialogStyles={dialogStyles}
                    modalContent={
                        <div className={classes.OrderRecallContainer}>
                            <DynamicDataGrid
                                dataGridClassName={classes.grid}
                                columns={columns}
                                allowHeaderFilting={false}
                                allowDelete={false}
                                actionButtonsVisibility={false}
                                standardKeyboardNavigation={false}
                                selection={'single'}
                                gridProps={{
                                    dataSource: invoiceLines || [],
                                    dataGridRef: gridRef,
                                    onSelectionChanged: handleOnSelectionChanged,
                                    onKeyDown: handleGridKeyDown,
                                    focusedRowEnabled: true,
                                    keyExpr: 'LineNumber'
                                }}
                            />
                        </div>
                    }
                    actions={actions}
                    dialogActionsButtons={true}
                    dialogActionsShadow={false}
                />
            </HotKeys>
        </div>
    );
};

export default withStyles(InvoiceLineSelectionDialogStyles)(React.memo(InvoiceLineSelectionDialog));
